import React, { useState } from 'react';
import PropTypes from 'prop-types';

const VARIANT_CLASSES = {
    info: 'bg-sky-600 text-white',
    success: 'bg-emerald-600 text-white',
    warning: 'bg-amber-500 text-slate-950',
    danger: 'bg-red-600 text-white'
};

const CTA_CLASSES = {
    info: 'border-white text-white hover:bg-white hover:text-sky-700',
    success: 'border-white text-white hover:bg-white hover:text-emerald-700',
    warning: 'border-slate-950 text-slate-950 hover:bg-slate-950 hover:text-white',
    danger: 'border-white text-white hover:bg-white hover:text-red-700'
};

const SiteBannerOverlay = ({ banner, onDismiss, onNeverShowAgain }) => {
    const [isDismissing, setIsDismissing] = useState(false);
    const [isDismissMenuOpen, setIsDismissMenuOpen] = useState(false);

    if (banner === null) {
        return null;
    }

    const variantClass = VARIANT_CLASSES[banner.variant];
    const ctaClass = CTA_CLASSES[banner.variant];
    if (!variantClass || !ctaClass) {
        throw new Error(`Unsupported site banner variant: ${banner.variant}`);
    }

    const dismissClass = isDismissing
        ? '-translate-y-[calc(100%+1rem)] opacity-0'
        : 'translate-y-0 opacity-100';

    const handleDismissMenu = () => {
        setIsDismissMenuOpen(true);
    };

    const handleHideForNow = () => {
        setIsDismissing(true);
        onDismiss(banner.id);
    };

    const handleNeverShowAgain = () => {
        setIsDismissing(true);
        onNeverShowAgain(banner.id);
    };

    return (
        <div
            data-testid="site-banner"
            className={`fixed top-4 left-4 right-4 z-[80] rounded-lg px-12 py-3 shadow-[0_18px_45px_rgba(15,23,42,0.45)] ring-1 ring-black/10 transition-all duration-200 ease-in-out ${dismissClass} ${variantClass}`}
            role="status"
            aria-live="polite"
        >
            <div className="flex items-center justify-center gap-4 text-center text-sm font-semibold">
                <span>{banner.message_text}</span>
                {banner.cta_label !== null && banner.cta_url !== null && (
                    <a
                        href={banner.cta_url}
                        className={`rounded border px-3 py-1 text-sm font-semibold ${ctaClass}`}
                    >
                        {banner.cta_label}
                    </a>
                )}
                {banner.is_closable && (
                    <button
                        type="button"
                        aria-label="Dismiss site banner"
                        data-testid="site-banner-dismiss"
                        className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-current text-xs font-bold leading-none"
                        onClick={handleDismissMenu}
                    >
                        X
                    </button>
                )}
                {isDismissMenuOpen && (
                    <div className="absolute right-3 top-full mt-2 w-44 rounded border border-slate-300 bg-white py-1 text-sm font-semibold text-slate-900 shadow-lg">
                        {banner.allow_permanent_dismissal && (
                            <button
                                type="button"
                                data-testid="site-banner-never-show-again"
                                className="block w-full px-3 py-2 text-left hover:bg-slate-100"
                                onClick={handleNeverShowAgain}
                            >
                                Never Show Again
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="site-banner-hide-for-now"
                            className="block w-full px-3 py-2 text-left hover:bg-slate-100"
                            onClick={handleHideForNow}
                        >
                            Hide for Now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

SiteBannerOverlay.propTypes = {
    banner: PropTypes.shape({
        id: PropTypes.number.isRequired,
        message_text: PropTypes.string.isRequired,
        variant: PropTypes.oneOf(['info', 'success', 'warning', 'danger']).isRequired,
        cta_label: PropTypes.string,
        cta_url: PropTypes.string,
        is_closable: PropTypes.bool.isRequired,
        allow_permanent_dismissal: PropTypes.bool.isRequired,
        repeat_policy: PropTypes.string,
        revision: PropTypes.number
    }),
    onDismiss: PropTypes.func.isRequired,
    onNeverShowAgain: PropTypes.func.isRequired
};

export default SiteBannerOverlay;
